// client/src/components/templates/contratos/ContratoTemplates.js - VERSÃO CORRIGIDA
import React, { useState, useCallback, useEffect } from 'react';
import { useTemplatesContrato, useTemplateContratoById, useMutateTemplateContrato } from '../../../hooks/useTemplateContrato';
import TemplateContratoForm from './TemplateContratoForm';
import TemplateContratoPreview from './TemplateContratoPreview';
import TemplateRow from '../TemplateRow';
import ActionsDrawer from '../../common/ActionsDrawer';
import { Loader2, AlertCircle, Eye, SquarePen, Trash2 } from 'lucide-react';

const ContratoTemplates = ({ 
    canManage, 
    isMatrizAdmin,
    showFormGlobal, 
    currentTemplateIdGlobal, 
    refreshTriggerGlobal, 
    onEditGlobal, 
    onCloseFormGlobal,
    onPreferenceChangeGlobal,
    isUpdatingPreferenceGlobal,
    currentTipo,
    // ✅ NOVAS PROPS PARA PREVIEW
    previewTemplateGlobal,
    onPreviewGlobal,
    onClosePreviewGlobal
}) => {
    const isCorrectTipo = currentTipo === 'contrato';
    const shouldFetchList = isCorrectTipo && !showFormGlobal && !previewTemplateGlobal;
    const shouldFetchDetails = isCorrectTipo && !!currentTemplateIdGlobal && showFormGlobal;

    const { data: templates, isLoading: isLoadingList, refetch, isError: isErrorList } = useTemplatesContrato({}, { enabled: shouldFetchList });
    const { data: templateDetails, isLoading: isLoadingDetails } = useTemplateContratoById(currentTemplateIdGlobal, { enabled: shouldFetchDetails });
    const { deleteTemplateContrato, isPendingContrato } = useMutateTemplateContrato();

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    useEffect(() => {
        if (shouldFetchList) refetch();
    }, [refreshTriggerGlobal, shouldFetchList, refetch]);

    const handleDelete = useCallback((id) => deleteTemplateContrato.mutate(id), [deleteTemplateContrato]);

    // ✅ USA A FUNÇÃO DO COMPONENTE PAI PARA PREVIEW
    const handlePreview = useCallback((template) => {
        onPreviewGlobal(template);
    }, [onPreviewGlobal]);

    // ✅ USA A FUNÇÃO DO COMPONENTE PAI PARA FECHAR PREVIEW
    const handleClosePreview = useCallback(() => {
        onClosePreviewGlobal();
    }, [onClosePreviewGlobal]);

    // Se estiver visualizando preview
    if (previewTemplateGlobal) {
        return (
            // ✅ SEM content-panel AQUI - O TemplateContratoPreview já tem seu próprio container
            <TemplateContratoPreview 
                template={previewTemplateGlobal} 
                onClose={handleClosePreview}
            />
        );
    }

    // Se estiver no formulário de edição/criação
    if (showFormGlobal && isCorrectTipo) {
        if (currentTemplateIdGlobal && isLoadingDetails) {
            return (
                <div className="content-panel">
                    <div className="panel-state-wrapper">
                        <Loader2 className="animate-spin"/> 
                        Carregando...
                    </div>
                </div>
            );
        }
        return (
            <div className="content-panel">
                <TemplateContratoForm
                    current={templateDetails}
                    onCloseForm={onCloseFormGlobal}
                />
            </div>
        );
    }

    // Se não for o tipo correto, não mostrar nada
    if (!isCorrectTipo) return null;

    // Lista de templates
    return (
        // ✅ ÚNICO content-panel para a lista
        <div className="content-panel">
            <div className="table-container">
                {isLoadingList ? (
                    <div className="panel-state-wrapper">
                        <Loader2 size={24} className="icon animate-spin"/>
                        Carregando templates de contrato...
                    </div>
                ) : null}

                {/* "error" nao existe como modificador — o CSS define
                    .panel-error (_panels.scss), entao esse estado nunca
                    tinha a cor de alerta nem o icone vermelho */}
                {isErrorList ? (
                    <div className="panel-state-wrapper panel-error">
                        <AlertCircle size={24} className="icon"/>
                        Erro ao carregar templates de contrato.
                    </div>
                ) : null}

                {!isLoadingList && !isErrorList && (
                    <div className="table-wrapper">
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Descrição</th>
                                    <th>Modificado em</th>
                                    <th className="text-center">Visibilidade / Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates?.length > 0 ? (
                                    templates.map(template => (
                                        <TemplateRow
                                            key={template._id}
                                            template={template}
                                            tipo="contrato"
                                            onOpenActions={setSelectedTemplate}
                                            onPreferenceChange={onPreferenceChangeGlobal}
                                            isUpdatingPreference={isUpdatingPreferenceGlobal}
                                            isMatrizAdmin={isMatrizAdmin}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="td-empty">
                                            Nenhum template de contrato encontrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ActionsDrawer
                open={!!selectedTemplate}
                onClose={() => setSelectedTemplate(null)}
                title={selectedTemplate?.nome || 'Template'}
                actions={selectedTemplate ? [
                    {
                        key: 'visualizar',
                        label: 'Visualizar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => { handlePreview(selectedTemplate); setSelectedTemplate(null); },
                    },
                    ...(canManage ? [{
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => { onEditGlobal(selectedTemplate._id); setSelectedTemplate(null); },
                        disabled: selectedTemplate.isPadrao && !selectedTemplate.account && !selectedTemplate.franquia && !isMatrizAdmin,
                    }] : []),
                    ...(canManage ? [{
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => { if (window.confirm('Tem certeza?')) { handleDelete(selectedTemplate._id); setSelectedTemplate(null); } },
                        destructive: true,
                        disabled: selectedTemplate.isPadrao && !selectedTemplate.account && !selectedTemplate.franquia && !isMatrizAdmin,
                        loading: isPendingContrato,
                    }] : []),
                ] : []}
            />
        </div>
    );
};

export default ContratoTemplates;